/**
 * MySuggestionDetailPane — CM's expanded view in /my-suggestions
 * (Block 6.C.2 / ADR-021).
 *
 * Pending row: shows old/new/rationale + Withdraw button (silent; no
 * email). Disposed row: shows old/new/rationale + read-only history
 * (status, reviewer_display, reviewed_at, review_note).
 *
 * Per ADR-017 F7: withdraw is reversible-via-resubmit; no confirm.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SuggestedEditWithJoinDisplay } from '@/lib/types';
import { suggestedEditsApi } from '@/lib/suggestedEdits';
import { invalidateAdminReviewKeys } from '@/lib/adminReview';
import { suggestionRefusalText } from '@/lib/suggestionRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import { humanizeFieldName, humanizeFieldValue } from '@/lib/fieldDisplay';
import { useJunctionLabelResolver } from '@/lib/useJunctionLabels';
import { refreshFamily } from '@/lib/refreshFamily';

export interface MySuggestionDetailPaneProps {
  tenantSlug: string;
  suggestion: SuggestedEditWithJoinDisplay;
  invalidationKey: ReadonlyArray<unknown>;
}

export function MySuggestionDetailPane({
  tenantSlug,
  suggestion,
  invalidationKey,
}: MySuggestionDetailPaneProps): ReactNode {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();

  // Item 5 — resolve junction slugs to display names for the diff render.
  const isJunction =
    suggestion.target_type === 'organization' &&
    (suggestion.field_name === 'subcategories' ||
      suggestion.field_name === 'specialties');
  const labelFor = useJunctionLabelResolver(tenantSlug, isJunction);
  const valueOpts = {
    targetType: suggestion.target_type,
    fieldName: suggestion.field_name,
    labelFor,
  };

  // The suggestion and the list to refresh (every status tab: the
  // family) are read at the press and travel as the variables.
  const withdrawMutation = useMutation({
    mutationFn: async (v: { id: string; family: readonly unknown[] }) => suggestedEditsApi.withdraw(tenantSlug, v.id),
    onSuccess: async (_res, v) => {
      await queryClient.invalidateQueries({ queryKey: v.family });
      // Block 6.F.5 §7.8: withdraw flips suggested_edits.status → drops
      // from pending count + emits no change_log row but the timeline
      // UNION carries the suggested_edits row → invalidate both.
      await invalidateAdminReviewKeys(queryClient);
    },
    onError: (e: Error) => setError(suggestionRefusalText(e, contact)),
  });

  return (
    <div className="px-3 py-3 border-t border-sage-800/[8%]">
      <div className="text-xs text-ink-soft mb-1">Field</div>
      <div className="text-sm font-medium text-sage-800 mb-3">
        {humanizeFieldName(suggestion.target_type, suggestion.field_name)}
      </div>

      <div className="text-xs text-ink-soft mb-1">Old → New</div>
      <div className="text-sm mb-3">
        <span className="bg-sand text-ink px-2 py-1 rounded-[10px]">
          {humanizeFieldValue(suggestion.old_value, valueOpts)}
        </span>
        <span className="mx-2 text-ink-soft">→</span>
        <span className="bg-sage-50 text-ink px-2 py-1 rounded-[10px]">
          {humanizeFieldValue(suggestion.new_value, valueOpts)}
        </span>
      </div>

      {suggestion.rationale && (
        <>
          <div className="text-xs text-ink-soft mb-1">Your rationale</div>
          <div className="text-sm italic mb-3 text-ink-soft">"{suggestion.rationale}"</div>
        </>
      )}

      <div className="text-xs text-ink-soft mb-3">
        Status: <span className="font-semibold">{suggestion.status}</span>
        {suggestion.reviewer_display && suggestion.reviewed_at && (
          <>
            {' · reviewed by '}
            {suggestion.reviewer_display} on{' '}
            {new Date(suggestion.reviewed_at).toLocaleDateString()}
          </>
        )}
      </div>

      {suggestion.review_note && (
        <>
          <div className="text-xs text-ink-soft mb-1">Reviewer note</div>
          <div className="text-sm italic mb-3 text-ink-soft">"{suggestion.review_note}"</div>
        </>
      )}

      {error && (
        <div role="alert" className="text-xs text-red-700 mb-2" data-testid="my-suggestion-error">
          ⚠ {error}
        </div>
      )}

      {suggestion.status === 'pending' && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => withdrawMutation.mutate({ id: suggestion.id, family: refreshFamily(invalidationKey) })}
            disabled={withdrawMutation.isPending}
            className="min-h-11 px-4 py-1 text-sm text-ink-soft border border-sage-800/[8%] rounded-pill hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw'}
          </button>
        </div>
      )}
    </div>
  );
}
